import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "../../components/AppLayout";
import API_BASE_URL from "../../config/api";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Chip,
  IconButton,
  Avatar,
  TextField,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { toast } from "react-toastify";
import { getInitials, getAvatarColor } from "../../utils/avatarUtils";
import { formatDecimalHours } from "../../utils/timeUtils";
import EditAttendanceDialog from "../../components/EditAttendanceDialog";

export default function StaffAttendanceAdmin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [attendanceData, setAttendanceData] = useState([]);
  // Always start with today's date on page load
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);

  // Clear localStorage on mount to always start with today
  useEffect(() => {
    localStorage.removeItem('staffAttendanceSelectedDate');
  }, []);

  useEffect(() => {
    fetchAttendance(selectedDate);

    // Refetch when window gains focus
    const handleFocus = () => {
      fetchAttendance(selectedDate);
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [selectedDate]);

  const fetchAttendance = async (date) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      
      const url = `${API_BASE_URL}/api/staff/attendance-by-date?date=${date}`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setAttendanceData(data);
      } else {
        const errorText = await response.text();
        console.error('❌ [fetchAttendance] Error response:', errorText);
        toast.error("Failed to fetch attendance data");
      }
    } catch (error) {
      console.error("❌ [fetchAttendance] Exception:", error);
      toast.error("Failed to load attendance data");
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (event) => {
    const newDate = event.target.value;
    setSelectedDate(newDate);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setSelectedStaff(null);
  };

  const handleEditSave = () => {
    // Refresh attendance data after successful save
    fetchAttendance(selectedDate);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Present":
        return "#4CAF50";
      case "Absent":
        return "#F44336";
      case "Late":
        return "#FF9800";
      default:
        return "#9E9E9E";
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return "N/A";
    if (/^\d{2}:\d{2}$/.test(timeString)) {
      return timeString;
    }
    if (/^\d{2}:\d{2}:\d{2}$/.test(timeString)) {
      return timeString.substring(0, 5);
    }
    return timeString;
  };

  if (loading) {
    return (
      <AppLayout>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <CircularProgress sx={{ color: "#C4A574" }} />
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box sx={{ 
        p: { xs: 2, md: 4 },
        bgcolor: "#0a0a0a",
        minHeight: "100vh"
      }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton
              onClick={() => navigate("/staff")}
              sx={{
                mr: 2,
                bgcolor: "#C4A574",
                color: "#000",
                "&:hover": { bgcolor: "#B39565" },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "#C4A574",
                fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" }
              }}
            >
              Attendance
            </Typography>
          </Box>
          
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CalendarTodayIcon sx={{ color: "#C4A574" }} />
            <TextField
              type="date"
              value={selectedDate}
              onChange={handleDateChange}
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "#2a2a2a",
                  color: "#C4A574",
                  "& fieldset": {
                    borderColor: "#C4A574",
                    borderWidth: 2,
                  },
                  "&:hover fieldset": {
                    borderColor: "#B39565",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#C4A574",
                  },
                  "& input": {
                    color: "#C4A574",
                    fontWeight: 600,
                  },
                },
              }}
            />
          </Box>
        </Box>

        <Typography variant="body2" sx={{ mb: 3, color: "#888" }}>
          {new Date(selectedDate).toLocaleDateString("en-IN", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </Typography>

        {/* Attendance Cards Grid */}
        <Grid container spacing={3} sx={{ justifyContent: 'center' }}>
          {attendanceData.length === 0 ? (
            <Grid item xs={12}>
              <Typography align="center" sx={{ color: "#888", py: 4 }}>
                No hourly staff attendance records for today
              </Typography>
            </Grid>
          ) : (
            attendanceData.map((staff) => {
              const initials = getInitials(staff.FullName);
              const avatarColor = getAvatarColor(staff.FullName);
              const hasAttendance = staff.Status !== null;

              return (
                <Grid item key={staff.StaffId} xs={12} sm={6} md={4}>
                  <Card
                    sx={{
                      borderRadius: 2,
                      bgcolor: "#1a1a1a",
                      border: hasAttendance
                        ? `2px solid ${getStatusColor(staff.Status)}`
                        : "2px solid #333",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
                        borderColor: "#C4A574",
                      },
                    }}
                  >
                    <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                      {/* Staff Info */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 2,
                        }}
                      >
                        <Avatar
                          src={staff.Photo ? `${API_BASE_URL}${staff.Photo}` : undefined}
                          sx={{
                            width: 60,
                            height: 60,
                            bgcolor: staff.Photo ? 'transparent' : avatarColor,
                            mr: 2,
                            fontSize: "1.5rem",
                            fontWeight: 600,
                            border: '2px solid #C4A574',
                            '& img': {
                              objectFit: 'cover'
                            }
                          }}
                        >
                          {!staff.Photo && initials}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: 600, color: "#fff" }}
                          >
                            {staff.FullName}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#C4A574" }}>
                            {staff.Designation}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Attendance Status */}
                      {hasAttendance ? (
                        <>
                          <Box sx={{ mb: 2 }}>
                            <Chip
                              label={staff.Status}
                              sx={{
                                bgcolor: getStatusColor(staff.Status),
                                color: "white",
                                fontWeight: 600,
                                fontSize: "0.9rem",
                                width: "100%",
                              }}
                            />
                          </Box>

                          {/* Time Details */}
                          <Box sx={{ bgcolor: "#2a2a2a", p: 2, borderRadius: 1, border: "1px solid #333" }}>
                            <Grid container spacing={1}>
                              <Grid item xs={6}>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#888" }}
                                >
                                  Punch In
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600, color: "#4CAF50" }}
                                >
                                  {staff.Status === "Absent" ? "N/A" : formatTime(staff.PunchInTime)}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#888" }}
                                >
                                  Punch Out
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600, color: "#F44336" }}
                                >
                                  {staff.Status === "Absent" 
                                    ? "N/A" 
                                    : (staff.PunchOutTime ? formatTime(staff.PunchOutTime) : "Not yet")}
                                </Typography>
                              </Grid>
                              <Grid item xs={12}>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#888" }}
                                >
                                  Total Work Hours
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600, color: "#C4A574" }}
                                >
                                  {staff.Status === "Absent"
                                    ? "N/A"
                                    : (!staff.PunchOutTime
                                      ? "In progress..."
                                      : formatDecimalHours(staff.TotalWorkHours))}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Box>

                          {/* Break Details */}
                          {staff.Breaks && staff.Breaks.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600, mb: 1, color: "#C4A574" }}
                              >
                                Break
                              </Typography>
                              {staff.Breaks.map((breakItem, index) => (
                                <Box
                                  key={index}
                                  sx={{
                                    bgcolor: "#333",
                                    p: 1.5,
                                    borderRadius: 1,
                                    mb: 1,
                                    border: "1px solid #444"
                                  }}
                                >
                                  <Grid container spacing={1}>
                                    <Grid item xs={6}>
                                      <Typography
                                        variant="caption"
                                        sx={{ color: "#888" }}
                                      >
                                        Start
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 600, color: "#FF9800" }}
                                      >
                                        {formatTime(breakItem.BreakStartTime)}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Typography
                                        variant="caption"
                                        sx={{ color: "#888" }}
                                      >
                                        End
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 600, color: "#FF9800" }}
                                      >
                                        {breakItem.BreakEndTime
                                          ? formatTime(breakItem.BreakEndTime)
                                          : "Active"}
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                </Box>
                              ))}
                              <Typography
                                variant="body2"
                                sx={{
                                  mt: 1,
                                  fontWeight: 600,
                                  color: "#FF9800",
                                }}
                              >
                                Total Break: {staff.TotalBreakMinutes || 0} minutes
                              </Typography>
                            </Box>
                          )}
                        </>
                      ) : (
                        <Box
                          sx={{
                            textAlign: "center",
                            py: 3,
                            bgcolor: "#2a2a2a",
                            borderRadius: 1,
                            border: "1px solid #444"
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{ color: "#F44336", fontWeight: 600 }}
                          >
                            Not Marked Yet
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#888" }}>
                            Attendance not recorded for today
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })
          )}
        </Grid>

        {/* Edit Attendance Dialog */}
        {selectedStaff && (
          <EditAttendanceDialog
            open={editDialogOpen}
            onClose={handleEditClose}
            staffData={selectedStaff}
            selectedDate={selectedDate}
            onSave={handleEditSave}
          />
        )}
      </Box>
    </AppLayout>
  );
}
